import * as React from "react";
import { Spinner, SpinnerSize, Tab, Tabs } from "@blueprintjs/core";
import { useCommonHooks, useEditRouteParams } from "../../../hooks";

import { useQuery } from "react-query";
import { ERoutes } from "../../../AppRouter";
import { BackButton, PageBase } from "../../../components";
import AgentDetailPanel from "./AgentDetailPanel";
import AgentFunctionGrid from "./AgentFunctionGrid";

export interface AgentItemPageProps {}

export const AgentItemPage: React.FunctionComponent<
  AgentItemPageProps
> = () => {
  const { t, router, apis, isSuperAdmin, user } = useCommonHooks();
  const { id, tab } = useEditRouteParams();

  const { data: displayName, isFetching: loadingDisplayName } = useQuery(
    ["agent-display-name", id],
    async () => {
      if (+id > 0) {
        return await apis.agents.getDisplayName(+id);
      }
      return t("NewAgent");
    }
  );

  const { data: isLinked } = useQuery(
    ["agent-is-linked-to-dossier", id],
    async () => {
      if (+id > 0) {
        return await apis.agents.agentIsLinkedToDossier(+id);
      }
      return false;
    }
  );

  const { data: canSeeAgent, isFetching: loadingCanSeePage } = useQuery(
    ["can-see-agent", [id, user.userId]],
    async () => {
      if (+id > 0) {
        return await apis.agents.canSeeAgentInfo(+id);
      }
      return true;
    },
    {
      staleTime: 60 * 60 * 1000 * 24,
    }
  );

  React.useEffect(() => {
    if (!canSeeAgent && !loadingCanSeePage) {
      router.navigate(ERoutes.agents);
    }
  }, [canSeeAgent, loadingCanSeePage, router]);

  const breadCrumbs = React.useMemo(() => {
    return [
      { text: t("Agents"), route: ERoutes.agents },
      {
        text: loadingDisplayName ? (
          <Spinner size={SpinnerSize.SMALL} />
        ) : (
          displayName.text
        ),
      },
    ];
  }, [displayName, loadingDisplayName, t]);

  return (
    <PageBase breadCrumbs={breadCrumbs} isLoading={loadingCanSeePage}>
      <Tabs
        selectedTabId={tab}
        onChange={(nextTabId) =>
          router.push(`${ERoutes.agents}/${id}/${nextTabId}`)
        }
      >
        <BackButton backRoute={ERoutes.agents} />
        <Tab
          id="details"
          title={t("AgentDetails")}
          panel={
            <AgentDetailPanel agentId={+id} agentIsLinkedToDossier={isLinked} />
          }
        />
        {+id > 0 && (
          <Tab
            id="functions"
            title={t("Functions")}
            panel={
              <AgentFunctionGrid
                agentId={+id}
                readonly={isLinked ?? !isSuperAdmin}
              />
            }
          />
        )}
      </Tabs>
    </PageBase>
  );
};

export default AgentItemPage;
